import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TranslationObject } from "../../models/translationModal";
import { scrollTop } from "../../untils";
import { ProfilePayload } from "../../models/userModels";
import ArticleItem from "./ArticleItem";
import { getDirectories } from "../../store/directories/selectors";
import HeaderMeta from "../../components/HeaderMeta/HeaderMeta";
import { useGetDeviPostsQuery } from "../../store/devi/devi.api";
import DeviCustomerLogoutModal from "../../components/Modals/DeviCustomerLogoutModal/DeviCustomerLogoutModal";
import TariffTable from "./Tariffs";
import DeviBanner from "../../components/DeviBanner/DeviBanner";
import "./ServiceCatalogSkeleton.css";

interface ServiceCatalogProps {
    entities?: TranslationObject;
    profile?: ProfilePayload;
    currentLanguage?: string;
}

const CatalogSkeleton: React.FC<
    Pick<ServiceCatalogProps, "currentLanguage">
> = ({ currentLanguage = "el" }) => {
    const { data: pages } = getDirectories();

    return (
        <main>
            <HeaderMeta
                currentLanguage={currentLanguage}
                customPages={pages?.custom_pages || []}
                textPages={pages?.text_pages || []}
            />
            <section className="catalog-page">
                <div className="posts-page__container">
                    <div className="catalog-page__header skeleton">
                        <div className="skeleton-title"></div>
                        <div className="skeleton-count"></div>
                    </div>

                    <div className="skeleton-description"></div>

                    <div className="accordion__content accordion__content-parent">
                        <div className="catalog-page__accordion">
                            <div className="accordion-posts">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="skeleton-category">
                                        <div className="skeleton-category-header">
                                            <div className="skeleton-category-title"></div>
                                            <div className="skeleton-category-count"></div>
                                        </div>
                                        <div className="skeleton-category-items">
                                            {[1, 2].map((j) => (
                                                <div
                                                    key={j}
                                                    className="skeleton-item"
                                                ></div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

const ServiceCatalog: React.FC<ServiceCatalogProps> = ({
    entities,
    profile,
    currentLanguage: currentLanguageProp,
}) => {
    const isSpecialist = profile?.user_role === "specialist";
    const isCustomer = !!profile && profile?.user_role !== "specialist";
    const { currentLanguage: langParam } = useParams<{
        currentLanguage?: string;
    }>();
    const supportedLanguages = new Set(["en", "ru", "el"]);
    const languageFromUrl =
        langParam && supportedLanguages.has(langParam) ? langParam : undefined;
    // URL — источник правды для маршрута :currentLanguage/posts; Redux может отставать на один кадр
    const resolvedLanguage =
        languageFromUrl ?? currentLanguageProp ?? "el";
    const [showDeviCustomerLogoutModal, setShowDeviCustomerLogoutModal] =
        useState(false);

    useEffect(() => {
        scrollTop();
    }, []);

    const { data, isLoading, isError } = useGetDeviPostsQuery({
        locale: resolvedLanguage,
    });
    const { data: pages } = getDirectories();

    const onCustomerRequestAccess = isCustomer
        ? () => setShowDeviCustomerLogoutModal(true)
        : undefined;

    if (isLoading) {
        return <CatalogSkeleton currentLanguage={resolvedLanguage} />;
    }

    if (isError) {
        return (
            <main>
                <HeaderMeta
                    currentLanguage={resolvedLanguage}
                    customPages={pages?.custom_pages || []}
                    textPages={pages?.text_pages || []}
                />
                <section className="catalog-page">
                    <div className="posts-page__container">
                        <div className="catalog-page__header">
                            <h1 className="catalog-page_title">
                                Ошибка загрузки
                            </h1>
                        </div>
                        <p className="catalog-page__description">
                            Не удалось загрузить каталог. Пожалуйста, попробуйте
                            позже.
                        </p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main>
            <HeaderMeta
                currentLanguage={resolvedLanguage}
                customPages={pages?.custom_pages || []}
                textPages={pages?.text_pages || []}
            />
            <DeviCustomerLogoutModal
                visible={showDeviCustomerLogoutModal}
                onClose={() => setShowDeviCustomerLogoutModal(false)}
                entities={entities}
            />
            <section className="catalog-page">
                <div className="posts-page__container">
                    <DeviBanner
                        banner={data?.banner}
                        entities={entities}
                        modifier="posts-page"
                        profile={profile}
                        onCustomerBuyClick={
                            isCustomer
                                ? () => setShowDeviCustomerLogoutModal(true)
                                : undefined
                        }
                    />
                    <div className="accordion__content accordion__content-parent">
                        <div className="catalog-page__accordion">
                            <div className="accordion-posts">
                                {data?.items?.map((category, index) => {
                                    const categoryKey = category.id
                                        ? `cat_${category.id}`
                                        : `cat_pos_${index}`;
                                    return (
                                        <ArticleItem
                                            key={categoryKey}
                                            category={category}
                                            entities={entities}
                                            isSpecialist={isSpecialist}
                                            hasSubscription={
                                                data?.subscribe?.active
                                            }
                                            paymentUrl={
                                                !data?.subscribe?.active
                                                    ? data?.subscribe
                                                          ?.payment_url
                                                    : undefined
                                            }
                                            onCustomerRequestAccess={
                                                onCustomerRequestAccess
                                            }
                                            profile={profile}
                                            onCustomerBuyClick={
                                                isCustomer
                                                    ? () =>
                                                          setShowDeviCustomerLogoutModal(
                                                              true
                                                          )
                                                    : undefined
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <TariffTable
                        priceList={data?.price_list}
                        entities={entities}
                        profile={profile}
                        onCustomerBuyClick={
                            isCustomer
                                ? () => setShowDeviCustomerLogoutModal(true)
                                : undefined
                        }
                    />
                </div>
            </section>
        </main>
    );
};

export default ServiceCatalog;
